import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import Drawer from '~/Common-Objects/UI/Drawer/Drawer.jsx'
import Icons from '~/Common-Objects/UI/Icons/index'
import Modal from '~/Common-Objects/Modal/index'
import useFeatures from './featureHook'
import useTiersHook from './tiersHook'
import axios from '~/data/http/axios'
import { alterFullpage, alterSiteDrawer } from '~/data/actions/siteActions'
import FormatNumberFuncs from '~/Classes/numberFormatFunctions'
import UpgradeDrawerTiers from '~/pages/cp/includes/upgrade/upgradeTiers'
import TierFeature from './tierFeature'

export default function UpsellModal({ feature, requiredTier, inline = false, onUpgrade = () => {}, ...props }) {
    const dispatch = useDispatch()
    const account = useSelector((state) => state.accountMaster)
    const Feature = useFeatures(feature, account.accountMaster, onUpgrade, props)
    const Tier = useTiersHook(requiredTier)
    const [newPlan, setNewPlan] = useState(null)

    const close = () => {
        if (props.onClose && typeof props.onClose === 'function') {
            props.onClose()
        } else {
            dispatch(alterFullpage(null))
        }
    }

    const upgrade = () => {
        if (props.onUpgradeClick && typeof props.onUpgradeClick === 'function') {
            return props.onUpgradeClick(newPlan)
        }
        if (props.onUpgradeClickSilently && typeof props.onUpgradeClickSilently === 'function') {
            props.onUpgradeClickSilently(newPlan)
        }
        dispatch(alterFullpage(null))
        const closeUpgradeDrawer = () => {
            if (inline) {
                dispatch(alterFullpage(null))
            } else {
                dispatch(alterSiteDrawer(false, true, 'right', null))
            }
            if (props.onUpgradeComplete && typeof props.onUpgradeComplete === 'function') {
                props.onUpgradeComplete()
            }
        }
        const drawer = (
            <UpgradeDrawerTiers onUpgrade={onUpgrade} newPlan={newPlan} upsell={feature} close={closeUpgradeDrawer} />
        )
        if (inline) {
            const fullpage = (
                <Drawer
                    isOpen={true}
                    standalone={true}
                    width={1000}
                    showClose={true}
                    close={() => {
                        dispatch(alterFullpage(null))
                    }}
                >
                    {drawer}
                </Drawer>
            )
            dispatch(alterFullpage(fullpage))
        } else {
            dispatch(alterSiteDrawer(true, true, 'right', drawer, true, 1000))
        }
    }

    useEffect(() => {
        if (requiredTier) {
            axios
                .get('/accountMaster/plan/next?tierName=' + requiredTier)
                .then((res) => {
                    setNewPlan(res.data)
                })
                .catch(() => {
                    dispatch(alterFullpage(null))
                })
        }
    }, [feature, requiredTier])

    if (!Feature || (requiredTier && !Tier) || (requiredTier && !newPlan)) {
        return null
    }

    let price = newPlan?.price
    let currency = '£'
    if (account.accountMaster?.currency !== 'GBP') {
        price = newPlan?.priceUsd
        currency = '$'
    }

    return (
        <Modal closeButton={true} blurBg={true} onCloseButton={close} className='upsellModal' width={820}>
            <div className='upsellModal__left'>
                <div className='upsellModal__taster'>{Feature.topTaster}</div>
                <div className='upsellModal__header'>
                    {Feature.header ? (
                        Feature.header
                    ) : (
                        <>
                            Upgrade to <span className='highlight'>{requiredTier}</span> to unlock {Feature.name}
                        </>
                    )}
                </div>
                <div className='upsellModal__image'>
                    <img src={Feature.image} alt={Feature.name} />
                </div>
                <div className='upsellModal__content'>{Feature.content}</div>
                {!Feature.customCta && (
                    <div className='upsellModal__button'>
                        <Button
                            iconRight={<Icons icon='arrowRight' />}
                            className='button-fullwidth button-primary'
                            onClick={upgrade}
                            dataAttribute={{ ph: 'upsell-modal-upgrade-button' }}
                        >
                            Upgrade to {requiredTier}
                        </Button>
                    </div>
                )}
            </div>
            <div className='upsellModal__right'>
                {!Feature.customRightPanel && (
                    <>
                        <div className='upsellModal__tier__name'>{requiredTier}</div>
                        <div className='upsellModal__tier__description'>{Tier?.desc}</div>
                        <div className='upsellModal__tier__price'>
                            <div className='upsellModal__tier__price'>
                                <span className='small'>{currency}</span>
                                {price}
                            </div>
                            <div className='upsellModal__tier__price_desc'>
                                Per month, billed monthly, for {FormatNumberFuncs.formatNumber(newPlan?.maxUniqueSends)}{' '}
                                contacts
                            </div>
                        </div>
                        <div className='upsellModal__tier__features'>
                            <div>What you'll also get on {requiredTier}:</div>
                            {Tier?.features.map((feature, index) => (
                                <TierFeature key={index} name={feature.name} help={feature.help} />
                            ))}
                        </div>
                    </>
                )}
                {Feature.customRightPanel}
            </div>
        </Modal>
    )
}
